import React from 'react'
import { ConfirmationRoleAction } from '../../../legacy-design-system/action'
import { DialogRenderer } from '../../../legacy-design-system/action/confirmation-action'
import {
  RestoreDialog,
  BulkRestoreDialog,
  BulkRestoreWithFiltersDialog
} from './restore-dialog'
import { Role, getLeastPermissiveRole } from '../../roles'
import { PureQueryOptions } from 'apollo-client'
import { IconUnarchive } from '@contrast-security-inc/design-system-foundations'
import { ConfirmationRoleActionProps } from '../../../legacy-design-system/action/confirmation-role-action'

interface MutationProps {
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

interface Application {
  readonly id: string
  readonly archived: boolean
  readonly role: Role
}

interface SingleProps extends MutationProps {
  readonly application: Application
}

interface BulkProps extends MutationProps {
  readonly applications: Application[]
  readonly showDisabled?: boolean
}

interface BulkWithFiltersProps extends MutationProps {
  readonly excludedApplications: Application[]
  readonly applications: Application[]
  readonly showDisabled?: boolean
  readonly filters: {
    readonly quickFilter: string
    readonly filterText: string
    readonly [key: string]: any
  }
}

interface Props extends Pick<ConfirmationRoleActionProps, 'size'> {
  readonly isActionValid: boolean
  readonly showDisabled?: boolean
  readonly role: Role
  readonly renderDialog: DialogRenderer
}

function RestoreAction({
  isActionValid,
  role,
  showDisabled,
  renderDialog,
  size
}: Props) {
  return (
    <ConfirmationRoleAction
      size={size}
      data-testid="action_unarchiveApp"
      isActionValid={isActionValid}
      role={role}
      requiredRole="EDIT"
      icon={IconUnarchive}
      iconTooltipMessage={{ id: 'action_unarchiveApp' }}
      renderDialog={renderDialog}
      iconTooltipPlacement="top"
      showDisabled={showDisabled}
      disabledTooltipMessage={{
        id: 'title_applications_insufficientUnarchivePermissions'
      }}
    />
  )
}

export function RestoreSingleAction({
  application: { id, role, archived },
  ...props
}: SingleProps) {
  return (
    <RestoreAction
      isActionValid={archived}
      role={role}
      renderDialog={(renderDialogProps) => {
        return (
          <RestoreDialog applicationID={id} {...props} {...renderDialogProps} />
        )
      }}
    />
  )
}

export function RestoreBulkAction({
  applications,
  showDisabled,
  ...props
}: BulkProps) {
  return (
    <RestoreAction
      isActionValid={applications.some((app) => app.archived)}
      showDisabled={showDisabled}
      size="md"
      role={getLeastPermissiveRole(applications.map((app) => app.role))}
      renderDialog={(renderDialogProps) => {
        return (
          <BulkRestoreDialog
            applicationIDs={applications.map((app) => app.id)}
            {...props}
            {...renderDialogProps}
          />
        )
      }}
    />
  )
}

export function RestoreBulkWithFiltersAction({
  excludedApplications,
  applications,
  showDisabled,
  filters,
  ...props
}: BulkWithFiltersProps) {
  return (
    <RestoreAction
      size="md"
      isActionValid={applications.some((app) => app.archived)}
      showDisabled={showDisabled}
      role={getLeastPermissiveRole(applications.map((app) => app.role))}
      renderDialog={(renderDialogProps) => {
        return (
          <BulkRestoreWithFiltersDialog
            excludedApplications={excludedApplications.map((app) => app.id)}
            filters={filters}
            {...props}
            {...renderDialogProps}
          />
        )
      }}
    />
  )
}
